import { Check, Columns3, Eye, RotateCcw } from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Dropdown } from './dropdown';
import { CONTROLE_BARRA } from './ui';

/**
 * Quais colunas cada tabela esconde, por tabela e por navegador.
 *
 * Preferência de VISUALIZAÇÃO, então mora no navegador da pessoa e não no
 * servidor: é escolha de quem olha, não do cadastro, e sincronizar isso entre
 * dispositivos custaria uma tabela e um endpoint para resolver um problema que
 * ninguém tem.
 *
 * Guarda o que está ESCONDIDO, não o que está visível. A diferença aparece no
 * dia em que uma coluna nova nasce: guardando os visíveis, ela nasceria
 * escondida para todo mundo que já mexeu na tabela — e ninguém entenderia por
 * que a coluna "não veio".
 */
interface EstadoColunas {
  ocultas: Record<string, string[]>;
  alternar: (tabela: string, coluna: string) => void;
  restaurar: (tabela: string) => void;
}

export const useColunas = create<EstadoColunas>()(
  persist(
    (set, get) => ({
      ocultas: {},

      alternar: (tabela, coluna) => {
        const atuais = get().ocultas[tabela] ?? [];
        const proximas = atuais.includes(coluna)
          ? atuais.filter((c) => c !== coluna)
          : [...atuais, coluna];
        set({ ocultas: { ...get().ocultas, [tabela]: proximas } });
      },

      restaurar: (tabela) => {
        const { [tabela]: _, ...resto } = get().ocultas;
        set({ ocultas: resto });
      },
    }),
    { name: 'social.colunas' },
  ),
);

/**
 * Referência ESTÁVEL para "nenhuma coluna escondida".
 *
 * Um `[]` literal dentro do seletor do store cria um array novo a cada render;
 * o zustand compara por identidade, conclui que o estado mudou, e a tela entra
 * em laço até estourar ("Maximum update depth exceeded"). O typecheck e os
 * testes passam — só o navegador denuncia.
 */
const NENHUMA: readonly string[] = Object.freeze([]);

/** As colunas escondidas de uma tabela, com identidade estável. */
export function useColunasOcultas(tabela: string): readonly string[] {
  return useColunas((e) => e.ocultas[tabela]) ?? NENHUMA;
}

export interface OpcaoColuna {
  chave: string;
  titulo: string;
}

/**
 * Seletor de colunas da tabela.
 *
 * Não deixa esconder a ÚLTIMA coluna visível: uma tabela sem coluna nenhuma é
 * um retângulo vazio, e quem chegou lá não tem pista de como voltar.
 */
export function SeletorColunas({
  tabela,
  opcoes,
}: {
  tabela: string;
  opcoes: readonly OpcaoColuna[];
}) {
  const ocultas = useColunasOcultas(tabela);
  const alternar = useColunas((e) => e.alternar);
  const restaurar = useColunas((e) => e.restaurar);

  const visiveis = opcoes.length - ocultas.length;

  return (
    <Dropdown
      rotulo="Escolher colunas"
      classeGatilho={`${CONTROLE_BARRA} px-3`}
      gatilho={
        <>
          <Columns3 className="size-4" aria-hidden />
          <span className="hidden sm:inline">Colunas</span>
          {ocultas.length > 0 && (
            <span className="bg-marca-100 text-marca-700 rounded-full px-1.5 text-xs font-medium">
              {visiveis}
            </span>
          )}
        </>
      }
      classePainel="w-56 p-1"
    >
      <p className="text-tinta-500 px-2 py-1.5 text-xs">
        {visiveis} de {opcoes.length} visíveis
      </p>
      {opcoes.map((opcao) => {
        const escondida = ocultas.includes(opcao.chave);
        const ultima = !escondida && visiveis === 1;
        return (
          <button
            key={opcao.chave}
            type="button"
            disabled={ultima}
            title={ultima ? 'A tabela precisa de pelo menos uma coluna.' : undefined}
            onClick={() => alternar(tabela, opcao.chave)}
            className="text-tinta-700 hover:bg-marca-600/[0.07] flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              {!escondida && <Check className="text-marca-600 size-3.5" aria-hidden />}
            </span>
            <span className="min-w-0 flex-1 truncate">{opcao.titulo}</span>
          </button>
        );
      })}
      {ocultas.length > 0 && (
        <button
          type="button"
          onClick={() => restaurar(tabela)}
          className="text-tinta-700 hover:bg-marca-600/[0.07] border-tinta-100 mt-1 flex w-full items-center gap-2 rounded-md border-t px-2 py-1.5 text-left text-sm transition-colors"
        >
          <RotateCcw className="size-3.5" aria-hidden />
          Mostrar todas
        </button>
      )}
    </Dropdown>
  );
}

/** Ícone usado pela tela quando quer sinalizar "há coluna escondida". */
export const IconeColunaOculta = Eye;
